import { IBulkEmailAttachment, ICandidate, ILocation } from 'wisestep-api';
import { getDateUsingFormat } from 'wisestep-base';

import { getLocation } from '../../../candidates/utils/CandidateHelper';

const getFormattedData = (date: string) => getDateUsingFormat(date, 'D MMMM YYYY');

export const getDefaultAttachments = (
  candidatesList: ICandidate[]
): [IBulkEmailAttachment[], string[]] => {
  const defaultAttachmentArray: IBulkEmailAttachment[] = [];
  const missingCandidates: string[] = [];
  candidatesList.forEach((candidate) => {
    defaultAttachmentArray.push({
      attachementName: candidate.resumeFileOriginalName ?? '',
      attachementURL: candidate.resumeLocation ? candidate.resumeLocation.split('?')[0] : '',
    });
    if (candidate.resumeLocation == '') {
      missingCandidates.push(candidate.email ?? '');
    }
  });
  return [defaultAttachmentArray, missingCandidates];
};

const generateColumnHeader = (column: Record<string, unknown>) => {
  return `<th colspan="1" rowspan="1" style="min-width: 48px; border: 2px solid #ced4da; padding: 3px 5px; vertical-align: top; box-sizing: border-box; position: relative; font-weight: bold; text-align: left; background-color: #f1f3f5;"><p>${column.parameter}</p></th>`;
};

const generateRowForCandidate = (
  candidate: ICandidate,
  column: Record<string, unknown>
): string => {
  if ((column.value as string) === 'currentLocation') {
    return `<td colspan="1" rowspan="1" style="min-width: 48px; border: 2px solid #ced4da; padding: 3px 5px; vertical-align: top; box-sizing: border-box; position: relative;"><p>${
      candidate[column.value as string]
        ? getLocation(candidate.currentLocation as ILocation)
        : 'Not Available'
    }</p></td>`;
  } else if ((column.value as string) === 'createdOn') {
    return `<td colspan="1" rowspan="1" style="min-width: 48px; border: 2px solid #ced4da; padding: 3px 5px; vertical-align: top; box-sizing: border-box; position: relative;"><p>${getFormattedData(
      candidate.createdOn ?? ''
    )}</p></td>`;
  } else if ((column.value as string) === 'preferredLocations') {
    return `<td colspan="1" rowspan="1" style="min-width: 48px; border: 2px solid #ced4da; padding: 3px 5px; vertical-align: top; box-sizing: border-box; position: relative;"><p>${
      candidate.preferredLocations && candidate.preferredLocations.length > 0
        ? candidate.preferredLocations?.map((location) => getLocation(location as ILocation))
        : 'Not Available'
    }</p></td>`;
  } else if ((column.value as string) === 'candidateSkills') {
    return `<td colspan="1" rowspan="1" style="min-width: 48px; border: 2px solid #ced4da; padding: 3px 5px; vertical-align: top; box-sizing: border-box; position: relative;"><p>${
      candidate.candidateSkills?.map((skill) => skill.skill).join(',') ?? ''
    }</p></td>`;
  } else if ((column.value as string) === 'expectedCompensation' && candidate.expectedBaseSalary) {
    return `<td colspan="1" rowspan="1" style="min-width: 48px; border: 2px solid #ced4da; padding: 3px 5px; vertical-align: top; box-sizing: border-box; position: relative;"><p>${
      candidate.expectedSalaryCurrency ? candidate.expectedSalaryCurrency + ' - ' : ''
    } ${candidate.expectedBaseSalary ? candidate.expectedBaseSalary + ' - ' : ''}  ${
      candidate.expectedBaseSalaryTimeSpan ?? ''
    }</p></td>`;
  } else if ((column.value as string) === 'currentCompensation' && candidate.currentSalary) {
    return `<td colspan="1" rowspan="1" style="min-width: 48px; border: 2px solid #ced4da; padding: 3px 5px; vertical-align: top; box-sizing: border-box; position: relative;"><p>${
      candidate.currentSalaryCurrency ? candidate.currentSalaryCurrency + ' - ' : ''
    } ${candidate.currentSalary ? candidate.currentSalary + ' - ' : ''}  ${
      candidate.currentSalaryTimeSpan ?? ''
    }</p></td>`;
  } else if ((column.value as string) === 'totalExperience') {
    return `<td colspan="1" rowspan="1" style="min-width: 48px; border: 2px solid #ced4da; padding: 3px 5px; vertical-align: top; box-sizing: border-box; position: relative;"><p>${
      candidate.totalYearsExperience ? candidate.totalYearsExperience + ' Years ' : ''
    } ${
      candidate.totalMonthsExperience ? (candidate.totalMonthsExperience % 12) + ' Months ' : ''
    } </p></td>`;
  } else if ((column.value as string) === 'relevantExperience') {
    return `<td colspan="1" rowspan="1" style="min-width: 48px; border: 2px solid #ced4da; padding: 3px 5px; vertical-align: top; box-sizing: border-box; position: relative;"><p>${
      candidate.relevantYearsExperience ? candidate.relevantYearsExperience + ' Years ' : ''
    } ${
      candidate.relevantMonthsExperience
        ? (candidate.relevantMonthsExperience % 12) + ' Months '
        : ''
    }  </p></td>`;
  }
  return `<td colspan="1" rowspan="1" style="min-width: 48px; border: 2px solid #ced4da; padding: 3px 5px; vertical-align: top; box-sizing: border-box; position: relative;"><p>${
    candidate[column.value as string] ?? ''
  }</p></td>`;
};

export const getTableHead = (columns: Record<string, unknown>[]) => {
  let body =
    '<table style="border-collapse: collapse; width: 100%; margin: 0; overflow-x: scroll;white-space:nowrap;"><tbody><tr>';

  columns.forEach((column) => (body = body + generateColumnHeader(column)));

  body = body + '</tr><tr>';

  return body;
};

export const getDefaultEmailBodyCustomized = (
  candidatesList: ICandidate[],
  columns: Record<string, unknown>[]
): string => {
  let body = getTableHead(columns);

  candidatesList.forEach((candidate) => {
    columns.forEach((column) => (body = body + generateRowForCandidate(candidate, column)));
    body = body + '</tr><tr>';
  });
  body = body.slice(0, -4);
  return body;
};

export const defaultSubmissionTableData = [
  {
    value: 'name',
    parameter: 'Name',
  },
  {
    value: 'email',
    parameter: 'Email',
  },
  {
    value: 'phoneNumber',
    parameter: 'Phone Number',
  },
  {
    value: 'createdOn',
    parameter: 'Submitted On',
  },
  {
    value: 'mappedJobTitle',
    parameter: 'Job Title',
  },
  {
    value: 'companyName',
    parameter: 'Client Name',
  },
  {
    value: 'mappedJobLocation',
    parameter: 'Work Location',
  },
  {
    value: 'currentLocation',
    parameter: 'Current Location',
  },
  {
    value: 'preferredLocations',
    parameter: 'Preferred Locations',
  },
  {
    value: 'totalExperience',
    parameter: 'Total Experience',
  },
  {
    value: 'expectedCompensation',
    parameter: 'Expected Compensation',
  },
  {
    value: 'visaType',
    parameter: 'Visa Type',
  },
  {
    value: 'relocationPreferences',
    parameter: 'Relocation Preferences',
  },
];

export const allSubmissionTableData = [
  {
    value: 'createdOn',
    parameter: 'Submitted On',
  },
  {
    value: 'mappedJobTitle',
    parameter: 'Job Title',
  },
  {
    value: 'name',
    parameter: 'Name',
  },
  {
    value: 'email',
    parameter: 'Email',
  },
  {
    value: 'companyName',
    parameter: 'Client Name',
  },
  {
    value: 'visaType',
    parameter: 'Visa Type',
  },
  {
    value: 'phoneNumber',
    parameter: 'Phone Number',
  },
  {
    value: 'currentLocation',
    parameter: 'Current Location',
  },
  {
    value: 'totalExperience',
    parameter: 'Total Experience',
  },
  {
    value: 'relocationPreferences',
    parameter: 'Relocation Preferences',
  },
  {
    value: 'expectedCompensation',
    parameter: 'Expected Compensation',
  },
  {
    value: 'applicationStage',
    parameter: 'Application Stage',
  },
  {
    value: 'applicationState',
    parameter: 'Application State',
  },
  {
    value: 'jobClientRecruiterName',
    parameter: 'Client SPOC',
  },
  {
    value: 'mappedJobLocation',
    parameter: 'Job Location',
  },
  {
    value: 'messengerId',
    parameter: 'Messenger ID',
  },

  {
    value: 'visaValidity',
    parameter: 'Visa Validity',
  },
  {
    value: 'jobAccountManagerName',
    parameter: 'Job Account Manager',
  },
  {
    value: 'nationality',
    parameter: 'Nationality',
  },
  {
    value: 'alternateEmail',
    parameter: 'Alternate Email',
  },

  {
    value: 'alternatePhoneNumber',
    parameter: 'Alternate Phone Number',
  },
  {
    value: 'jobPreferences',
    parameter: 'Job Preferences',
  },
  {
    value: 'jobType',
    parameter: 'Job Type',
  },
  {
    value: 'linkedInURL',
    parameter: 'LinkedIn URL',
  },
  {
    value: 'candidateID',
    parameter: 'Candidate ID',
  },

  {
    value: 'workAuthorization',
    parameter: 'Work Authorization',
  },
  {
    value: 'source',
    parameter: 'Source',
  },
  {
    value: 'candidateSkills',
    parameter: 'Skills',
  },
  {
    value: 'otherDocuments',
    parameter: 'Other Documents',
  },
  {
    value: 'relevantExperience',
    parameter: 'Relevant Experience',
  },
  {
    value: 'highestEducation',
    parameter: 'Highest Education',
  },
  {
    value: 'institutesAttended',
    parameter: 'Institutes Attended',
  },
  {
    value: 'currentJobTitle',
    parameter: 'Current Job Title',
  },
  {
    value: 'previousJobTitles',
    parameter: 'Previous Job Titles',
  },
  {
    value: 'currentEmployer',
    parameter: 'Current Employer',
  },
  {
    value: 'previousEmployers',
    parameter: 'Previous Employers',
  },
  {
    value: 'certifications',
    parameter: 'Certifications',
  },
  {
    value: 'preferredLocations',
    parameter: 'Preferred Location',
  },

  {
    value: 'currentCompensation',
    parameter: 'Current Compensation',
  },

  {
    value: 'communicationPreferences',
    parameter: 'Communication Preferences',
  },
  {
    value: 'availableTimeSlot',
    parameter: 'Available Time Slot',
  },
  {
    value: 'noticePeriod',
    parameter: 'Notice Period',
  },
  {
    value: 'reasonForChange',
    parameter: 'Reason For Change',
  },
  {
    value: 'interviewingAnywhereElse',
    parameter: 'Interviewing Anywhere Else',
  },
  {
    value: 'assessment',
    parameter: 'Recruiter Assessment Notes',
  },
  {
    value: 'questions',
    parameter: 'Questions',
  },
  {
    value: 'familyWillingToRelocate',
    parameter: 'Family Willing To Relocate',
  },
  {
    value: 'otherAllowance',
    parameter: 'Other Allowance',
  },
  {
    value: 'otherInterviewStage',
    parameter: 'Other Interview Stage',
  },
  {
    value: 'workAuthorizationCategory',
    parameter: 'Work Authorization Category',
  },
  {
    value: 'currentJobIncludeTravel',
    parameter: 'Current Job Include Travel',
  },
  {
    value: 'willingToTravel',
    parameter: 'Willing to Relocate',
  },
  {
    value: 'expectedAllowance',
    parameter: 'Expected Allowances & Benefits',
  },
  {
    value: 'stageDate',
    parameter: 'Stage Date',
  },
  {
    value: 'passportType',
    parameter: 'Passport Type',
  },
  {
    value: 'passportNumber',
    parameter: 'Passport Number',
  },
  {
    value: 'postalAddress',
    parameter: 'Postal Address',
  },
  {
    value: 'ssnNumber',
    parameter: 'SSN Number',
  },
  {
    value: 'gender',
    parameter: 'Gender',
  },
  {
    value: 'age',
    parameter: 'Age',
  },
  {
    value: 'createdBy',
    parameter: 'Submitted Recruiter Email',
  },
  {
    value: 'recruiterName',
    parameter: 'Submitted Recruiter Name',
  },
];
